import { NextRequest, NextResponse } from 'next/server'
import { and, count, eq } from 'drizzle-orm'
import { requireExpertApiUser } from '@/lib/role-guard'
import { db } from '@/db'
import { tenantMemberships } from '@/db/schema'

const VALID_ROLES = ['line_manager', 'expert', 'admin'] as const
type UserRole = (typeof VALID_ROLES)[number]

export async function PATCH(
  request: NextRequest,
  { params }: { params: Promise<{ userId: string }> },
) {
  const auth = await requireExpertApiUser()
  if (!auth.ok) return auth.response

  const { userId } = await params

  if (userId === auth.user.id) {
    return NextResponse.json({ error: 'You cannot modify your own account' }, { status: 422 })
  }

  const body = await request.json().catch(() => null)
  if (!body || typeof body !== 'object') {
    return NextResponse.json({ error: 'Request body is required' }, { status: 400 })
  }

  const { role, isActive } = body as { role?: unknown; isActive?: unknown }

  if (role !== undefined && !VALID_ROLES.includes(role as UserRole)) {
    return NextResponse.json({ error: 'role must be line_manager, expert, or admin' }, { status: 400 })
  }
  if (isActive !== undefined && typeof isActive !== 'boolean') {
    return NextResponse.json({ error: 'isActive must be a boolean' }, { status: 400 })
  }
  if (role === undefined && isActive === undefined) {
    return NextResponse.json({ error: 'Provide role or isActive to update' }, { status: 400 })
  }

  // Fetch the target membership (must be in same tenant)
  const [target] = await db
    .select({
      id: tenantMemberships.id,
      role: tenantMemberships.role,
      isActive: tenantMemberships.isActive,
    })
    .from(tenantMemberships)
    .where(
      and(
        eq(tenantMemberships.userId, userId),
        eq(tenantMemberships.tenantId, auth.user.tenantId),
      )
    )
    .limit(1)

  if (!target) {
    return NextResponse.json({ error: 'User not found' }, { status: 404 })
  }

  // Guard: prevent removing the last admin
  const newRole = (role as UserRole | undefined) ?? target.role
  const newIsActive = isActive ?? target.isActive
  const wouldLoseAdmin =
    target.role === 'admin' && (newRole !== 'admin' || newIsActive === false)

  if (wouldLoseAdmin) {
    const [{ value: adminCount }] = await db
      .select({ value: count() })
      .from(tenantMemberships)
      .where(
        and(
          eq(tenantMemberships.tenantId, auth.user.tenantId),
          eq(tenantMemberships.role, 'admin'),
          eq(tenantMemberships.isActive, true),
        )
      )

    if (Number(adminCount) <= 1) {
      return NextResponse.json({ error: 'Cannot remove the last admin' }, { status: 422 })
    }
  }

  const update: Partial<{ role: UserRole; isActive: boolean; updatedAt: Date }> = {
    updatedAt: new Date(),
  }
  if (role !== undefined) update.role = role as UserRole
  if (isActive !== undefined) update.isActive = isActive as boolean

  const [updated] = await db
    .update(tenantMemberships)
    .set(update)
    .where(eq(tenantMemberships.id, target.id))
    .returning()

  return NextResponse.json(updated)
}
